import { useFormContext } from 'react-hook-form';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Input, Textarea } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import { IntegrationCustomParameterField } from '../../../model';
import type { IntegrationFormValue } from '../../../integrationFormSchema';

export function IntegrationNumberField({
  name,
  label,
  hint,
  max,
  prefix,
  suffix,
}: {
  name: 'targetCpm' | 'bidFloor' | 'qps' | 'shareOfVoice' | 'biddingTmax';
  label: string;
  hint?: string | undefined;
  max?: number | undefined;
  prefix?: string | undefined;
  suffix?: string | undefined;
}) {
  const { register, formState } = useFormContext<IntegrationFormValue>();
  let inputClassName;
  if (prefix) inputClassName = 'pl-7';
  else if (suffix) inputClassName = 'pr-8';

  return (
    <DashboardPageFormField
      label={label}
      htmlFor={`integration-${name}`}
      error={formState.errors[name]?.message}
      hint={hint}
    >
      <div className="relative">
        {prefix ? (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
            {prefix}
          </span>
        ) : null}
        <Input
          id={`integration-${name}`}
          type="number"
          min="0"
          max={max}
          step="any"
          className={inputClassName}
          aria-invalid={Boolean(formState.errors[name])}
          {...register(name, { valueAsNumber: true })}
        />
        {suffix ? (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
            {suffix}
          </span>
        ) : null}
      </div>
    </DashboardPageFormField>
  );
}

export function IntegrationCustomParameterInput({
  field,
  required,
}: {
  field: IntegrationCustomParameterField;
  required: boolean;
}) {
  const { register, formState } = useFormContext<IntegrationFormValue>();
  const hint = integrationCustomFieldHint(field);
  const label =
    field === IntegrationCustomParameterField.ImpExt
      ? dashboardT('integrations.impExtJson')
      : dashboardT(`integrations.${field}`);
  const commonProps = {
    id: `integration-${field}`,
    required,
    'aria-invalid': Boolean(formState.errors[field]),
    ...register(field),
  };

  return (
    <DashboardPageFormField
      label={label}
      htmlFor={`integration-${field}`}
      required={required}
      error={formState.errors[field]?.message}
      hint={hint}
    >
      {field === IntegrationCustomParameterField.ImpExt ? (
        <Textarea {...commonProps} rows={4} className="font-mono text-sm" />
      ) : (
        <Input {...commonProps} />
      )}
    </DashboardPageFormField>
  );
}

function integrationCustomFieldHint(field: IntegrationCustomParameterField) {
  if (
    field === IntegrationCustomParameterField.ImpExt ||
    field === IntegrationCustomParameterField.GroupId ||
    field === IntegrationCustomParameterField.BidRequestExtCo ||
    field === IntegrationCustomParameterField.BidRequestExt
  )
    return dashboardT(`integrations.hints.${field}`);
  if (
    field === IntegrationCustomParameterField.SmartAdServerPageId ||
    field === IntegrationCustomParameterField.SmartAdServerNetworkId ||
    field === IntegrationCustomParameterField.SmartAdServerFormatId
  )
    return dashboardT('integrations.hints.smartAdServerOnly');
  return undefined;
}
